import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  iamApi as iamHttpApi,
  type AddUserToGroupRequest,
  type BatchAddUserToGroupRequest,
  type CreateIAMUserGroupRequest,
  type ListIAMUserGroupsRequest,
  type UpdateIAMUserGroupRequest,
} from '@/api/iam.api';

const QK = ['iam', 'groups'] as const;

/** 真实 IAM 用户组列表；调用方决定正常分页或以 limit: -1 获取下拉全集。 */
export function useIamUserGroupList(params: ListIAMUserGroupsRequest, enabled = true) {
  return useQuery({
    queryKey: [...QK, 'api-list', params] as const,
    queryFn: () => iamHttpApi.listIAMUserGroups(params),
    placeholderData: keepPreviousData,
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: params.limit === -1 ? 5 * 60 * 1000 : 0,
  });
}

export function useCreateIamUserGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateIAMUserGroupRequest) => {
      const response = await iamHttpApi.createIAMUserGroup(body);
      if (response.code !== 0) {
        throw new Error(response.message || '创建用户组失败');
      }
      return response;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });
}

export function useUpdateIamUserGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateIAMUserGroupRequest) => {
      const response = await iamHttpApi.updateIAMUserGroup(body);
      if (response.code !== 0) {
        throw new Error(response.message || '更新用户组失败');
      }
      return response;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK });
      qc.invalidateQueries({ queryKey: ['iam', 'users'] });
    },
  });
}

export function useAddUserToGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: AddUserToGroupRequest) => {
      const response = await iamHttpApi.addUserToGroup(body);
      if (response.code !== 0) {
        throw new Error(response.message || '调整用户组成员失败');
      }
      return response;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK });
      qc.invalidateQueries({ queryKey: ['iam', 'users'] });
    },
  });
}

export function useBatchAddUserToGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: BatchAddUserToGroupRequest) => {
      const response = await iamHttpApi.batchAddUserToGroup(body);
      if (response.code !== 0) {
        throw new Error(response.message || '添加用户至用户组失败');
      }
      return response;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK });
      qc.invalidateQueries({ queryKey: ['iam', 'users'] });
    },
  });
}

export function useDeleteIamUserGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (groupId: string) => iamHttpApi.deleteIAMUserGroup({ group_id: groupId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK });
      qc.invalidateQueries({ queryKey: ['iam', 'users'] });
    },
  });
}

export function useIamGroup(id: string | undefined) {
  return useQuery({
    queryKey: [...QK, 'detail', id] as const,
    queryFn: () => iamHttpApi.getIAMUserGroup({ id: id as string }),
    enabled: !!id,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: 'always',
    staleTime: 0,
  });
}

/** 编辑用户组时可选的全部策略；is_grant 标识当前用户组已授权、成员可继承的策略。 */
export function useIamGroupPolicies(groupId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['iam', 'policies', 'group-edit', groupId] as const,
    queryFn: async () => {
      const response = await iamHttpApi.searchIdentityPolicies({
        identity_type: 'user_group',
        identity: groupId as string,
        limit: '100',
        offset: '0',
        order_by: '-created_at',
      });
      if (response.code !== 0) {
        throw new Error(response.message || '查询用户组继承策略失败');
      }
      return response.data.policies;
    },
    enabled: enabled && !!groupId,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/** 用户组详情中的策略授权；search 传用户组 ID，并限定主体类型。 */
export function useIamGroupPolicyGrants(groupId: string | undefined) {
  return useQuery({
    queryKey: ['iam', 'grants', 'group', groupId] as const,
    queryFn: async () => {
      const response = await iamHttpApi.searchPolicyGrants({
        identity_type: 'user_group',
        limit: 100,
        offset: '0',
        search: groupId as string,
      });
      if (response.code !== 0) {
        throw new Error(response.message || '查询用户组策略授权失败');
      }
      return response;
    },
    enabled: !!groupId,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: 'always',
    staleTime: 0,
  });
}
